import {
	CheckCircleOutlined,
	CloudSyncOutlined,
	CloudUploadOutlined
} from "@ant-design/icons";
import {
	Button,
	Card,
	Empty,
	Spin,
	Table,
	Tag,
	Tooltip
} from "antd";
import { useEffect, useState } from "react";
import { getAllCajas } from "../../services/cajaService";
import { getSyncConfig, syncAll } from "../../services/syncService";
import { getAllTPVs } from "../../services/tpvService";

const CajaList = ({ refreshTrigger, onEditCaja }) => {
	const [cajas, setCajas] = useState([]);
	const [tpvs, setTPVs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [syncingAll, setSyncingAll] = useState(false);
	const isMobile = window.innerWidth < 768;
	const [config, setConfig] = useState({
		interval: 30,
		intervalUnit: "segundos",
	});

	// Cargar la configuración al inicio
	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const syncConfig = await getSyncConfig();
				setConfig(syncConfig);
			} catch (error) {
				console.error("Error al cargar configuración:", error);
			}
		};

		fetchConfig();
	}, []);

	// Cargar TPVs al inicio
	useEffect(() => {
		const loadTPVs = async () => {
			try {
				const tpvList = await getAllTPVs();
				setTPVs(tpvList);
			} catch (error) {
				console.error("Error al cargar Tiendas:", error);
			}
		};

		loadTPVs();
	}, []);

	// Cargar cajas al inicio y cuando cambie el trigger
	useEffect(() => {
		loadCajas();
	}, [refreshTrigger]);

	// Actualizar la lista según el intervalo configurado
	useEffect(() => {
		const interval = setInterval(() => {
			loadCajas();
		}, config.interval * (config.intervalUnit === "segundos" ? 1000 : config.intervalUnit === "minutos" ? 60000 : 3600000)); // Convertir a milisegundos

		// Limpiar intervalo al desmontar
		return () => clearInterval(interval);
	}, [config]);

	const loadCajas = async () => {
		try {
			console.log("Cargando cajas...");
			setLoading(true);
			const cajaList = await getAllCajas();
			setCajas(cajaList);
		} catch (error) {
			window.messageApi.error("Error al cargar cajas: " + error.message);
		} finally {
			setLoading(false);
		}
	};

	// const handleDelete = async (id) => {
	// 	try {
	// 		await deleteCaja(id);
	// 		// Actualizar la lista de cajas
	// 		setCajas(cajas.filter((caja) => caja.id !== id));
	// 		window.messageApi.success("Caja eliminada correctamente");
	// 	} catch (error) {
	// 		window.messageApi.error("Error al eliminar caja: " + error.message);
	// 	}
	// };

	// const handleEdit = (caja) => {
	// 	if (onEditCaja) {
	// 		onEditCaja(caja);
	// 	}
	// };

	// Renderizar el estado de sincronización
	const renderSyncStatus = (pendingSync, lastSynced) => {
		if (pendingSync === 1) {
			return (
				<Tooltip title="Pendiente de sincronizar">
					<Tag icon={<CloudUploadOutlined />} color="warning">
						Pendiente
					</Tag>
				</Tooltip>
			);
		} else {
			const syncDate = lastSynced
				? new Date(lastSynced).toLocaleString()
				: "Nunca";

			return (
				<Tooltip title={`Última sincronización: ${syncDate}`}>
					<Tag icon={<CheckCircleOutlined />} color="success">
						Sincronizado
					</Tag>
				</Tooltip>
			);
		}
	};

	// Sincronizar todas las cajas
	const handleSyncAll = async () => {
		try {
			setSyncingAll(true);
			await syncAll();
			// Recargar la lista para reflejar los cambios
			await loadCajas();
			window.messageApi.success("Sincronización completada");
		} catch (error) {
			window.messageApi.error(
				"Error durante la sincronización: " + error.message
			);
		} finally {
			setSyncingAll(false);
		}
	};

	// Función para obtener el nombre del TPV por su No
	const getTPVNameByNo = (tpvNo) => {
		if (!tpvNo) return "Sin Tienda";
		const tpv = tpvs.find((t) => t.No === tpvNo);
		return tpv ? tpv.nombre : `TPV ${tpvNo}`;
	};

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
		},
		{
			title: "No.",
			dataIndex: "No",
			key: "No",
			render: (No) => No || "Pendiente",
		},
		{
			title: "Nombre",
			dataIndex: "nombre",
			key: "nombre",
		},
		{
			title: "TPV",
			dataIndex: "TPV",
			key: "TPV",
			render: (tpvNo) => getTPVNameByNo(tpvNo),
		},
		{
			title: "Estado",
			key: "syncStatus",
			render: (_, record) =>
				renderSyncStatus(record.pendingSync, record.lastSynced),
		},
		// {
		// 	title: "Acciones",
		// 	key: "actions",
		// 	render: (_, record) => (
		// 		<Space size="middle">
		// 			<Button
		// 				type="primary"
		// 				icon={<EditOutlined />}
		// 				size="small"
		// 				onClick={() => handleEdit(record)}
		// 			>
		// 				Editar
		// 			</Button>
		// 			<Popconfirm
		// 				title="¿Está seguro de que desea eliminar esta caja?"
		// 				onConfirm={() => handleDelete(record.id)}
		// 				okText="Sí"
		// 				cancelText="No"
		// 			>
		// 				<Button danger icon={<DeleteOutlined />} size="small">
		// 					Eliminar
		// 				</Button>
		// 			</Popconfirm>
		// 		</Space>
		// 	),
		// },
	];

	if (loading) {
		return <Spin size="large" />;
	}

	if (cajas.length === 0) {
		return <Empty description="No hay cajas registradas aún" />;
	}

	return (
		<div className="caja-list-container">
			<Card
				title={isMobile ? "Cajas" : "Cajas Registradas"}
				extra={
					<Button
						icon={<CloudSyncOutlined />}
						onClick={handleSyncAll}
						loading={syncingAll}
						type="primary"
					>
						{isMobile ? "" : "Sincronizar Todas"}
					</Button>
				}
			>
				<Table
					columns={columns}
					dataSource={cajas.map((caja) => ({ ...caja, key: caja.id }))}
					pagination={{ pageSize: 5 }}
					bordered
					size="middle"
					scroll={{ x: "max-content" }}
				/>
			</Card>
		</div>
	);
};

export default CajaList;
